/**
 * NotificationsTab - Per-user notification preferences
 *
 * Replaces the old inline NotificationsTab in UserProfile.jsx.
 * Handles email + push preferences per event type, plus
 * tenant-level Slack/Teams webhook integrations (admin only).
 *
 * Import in UserProfile.jsx:
 *   import NotificationsTab from '../components/NotificationsTab';
 *
 * Usage:
 *   {activeTab === 'notifications' && <NotificationsTab user={user} />}
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Mail, Bell, Save, Loader2, CheckCircle, AlertTriangle,
  Plus, Trash2, Send, ExternalLink, ToggleLeft, ToggleRight,
  Hash, MessageSquare, Webhook, ChevronDown, ChevronUp, Info
} from 'lucide-react';
import useAuthStore from '../stores/authStore';
import PushNotificationSettings from './PushNotificationSettings';
import fetchWithTenant from '../utils/fetchWithTenant';

// Platform icons/colors
const PLATFORM_CONFIG = {
  SLACK: { label: 'Slack', color: '#E01E5A', icon: Hash },
  TEAMS: { label: 'Microsoft Teams', color: '#6264A7', icon: MessageSquare },
  CUSTOM: { label: 'Custom Webhook', color: 'var(--accent-primary)', icon: Webhook },
};

// Event type definitions - must match backend EVENT_TYPE_META
const EVENT_TYPES = {
  deviceOffline:   { label: 'Device Offline',   desc: 'A monitored device stops responding', channels: ['email', 'push'] },
  deviceRecovered: { label: 'Device Recovered', desc: 'A previously offline device comes back online', channels: ['email', 'push'] },
  alertCritical:   { label: 'Critical Alerts',  desc: 'High-severity alerts from alert rules', channels: ['email', 'push'] },
  alertWarning:    { label: 'Warning Alerts',    desc: 'Medium-severity threshold warnings', channels: ['email', 'push'] },
  dailyDigest:     { label: 'Daily Digest',      desc: 'Morning summary of the last 24 hours', channels: ['email'] },
  weeklyReport:    { label: 'Weekly Report',     desc: 'Weekly health and activity summary', channels: ['email'] },
  systemEvents:    { label: 'System Events',     desc: 'VMS disconnects, agent offline, lockdowns', channels: ['email', 'push'] },
  intercomCalls:   { label: 'Intercom Calls',    desc: 'Incoming intercom call notifications', channels: ['push'] },
  accessEvents:    { label: 'Access Events',     desc: 'Door access granted, denied, or forced open', channels: ['email', 'push'] },
};

const NotificationsTab = ({ user }) => {
  const { user: authUser } = useAuthStore();
  const isAdmin = authUser?.role === 'admin' || authUser?.isPlatformAdmin;

  const [prefs, setPrefs] = useState(null);
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null
  const [dirty, setDirty] = useState(false);
  const [showWebhookForm, setShowWebhookForm] = useState(false);
  const [webhookExpanded, setWebhookExpanded] = useState(true);
  const [testingId, setTestingId] = useState(null);

  // Load preferences + webhooks
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [prefsRes, webhooksRes] = await Promise.all([
        fetchWithTenant('/api/notification-prefs/me'),
        isAdmin ? fetchWithTenant('/api/notification-prefs/webhooks') : Promise.resolve(null),
      ]);

      const prefsData = await prefsRes.json();
      setPrefs(prefsData.preferences || {});

      if (webhooksRes) {
        const webhooksData = await webhooksRes.json();
        setWebhooks(Array.isArray(webhooksData) ? webhooksData : []);
      }
    } catch (err) {
      console.error('Failed to load notification prefs:', err);
    } finally {
      setLoading(false);
    }
  };

  // Toggle a single preference
  const togglePref = useCallback((channel, eventType) => {
    setPrefs(prev => {
      const updated = { ...prev };
      updated[channel] = { ...(updated[channel] || {}) };
      updated[channel][eventType] = !updated[channel][eventType];
      return updated;
    });
    setDirty(true);
    setSaveStatus(null);
  }, []);

  // Toggle all for a channel
  const toggleAllForChannel = useCallback((channel, enabled) => {
    setPrefs(prev => {
      const updated = { ...prev };
      updated[channel] = { ...(updated[channel] || {}) };
      Object.keys(EVENT_TYPES).forEach(key => {
        if (EVENT_TYPES[key].channels.includes(channel)) {
          updated[channel][key] = enabled;
        }
      });
      return updated;
    });
    setDirty(true);
    setSaveStatus(null);
  }, []);

  // Save preferences
  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      const res = await fetchWithTenant('/api/notification-prefs/me', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(prefs),
      });
      if (res.ok) {
        setSaveStatus('success');
        setDirty(false);
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        setSaveStatus('error');
      }
    } catch {
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  // Test webhook
  const handleTestWebhook = async (id) => {
    setTestingId(id);
    try {
      const res = await fetchWithTenant(`/api/notification-prefs/webhooks/${id}/test`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      const result = await res.json();
      if (result.success) {
        alert('Test notification sent successfully!');
      } else {
        alert('Test failed: ' + (result.error || `HTTP ${result.status}`));
      }
    } catch (err) {
      alert('Test failed: ' + err.message);
    } finally {
      setTestingId(null);
    }
  };

  // Delete webhook
  const handleDeleteWebhook = async (id) => {
    if (!confirm('Remove this integration? Alert notifications will stop being sent to this channel.')) return;
    try {
      await fetchWithTenant(`/api/notification-prefs/webhooks/${id}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' } });
      setWebhooks(prev => prev.filter(w => w.id !== id));
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  // Toggle webhook enabled
  const handleToggleWebhook = async (id, enabled) => {
    try {
      await fetchWithTenant(`/api/notification-prefs/webhooks/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      setWebhooks(prev => prev.map(w => w.id === id ? { ...w, enabled } : w));
    } catch (err) {
      alert('Failed to update: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="notif-prefs-loading">
        <Loader2 size={24} className="spin" />
        <span>Loading preferences...</span>
      </div>
    );
  }

  // Count enabled per channel
  const emailCount = Object.entries(prefs?.email || {}).filter(([k, v]) => v && EVENT_TYPES[k]).length;
  const pushCount = Object.entries(prefs?.push || {}).filter(([k, v]) => v && EVENT_TYPES[k]).length;
  const emailTotal = Object.keys(EVENT_TYPES).filter(k => EVENT_TYPES[k].channels.includes('email')).length;
  const pushTotal = Object.keys(EVENT_TYPES).filter(k => EVENT_TYPES[k].channels.includes('push')).length;

  return (
    <div className="notif-prefs">
      {/* Save bar */}
      {dirty && (
        <div className="notif-prefs-save-bar">
          <span>You have unsaved changes</span>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      {saveStatus === 'success' && (
        <div className="notif-prefs-status success">
          <CheckCircle size={16} />
          Preferences saved successfully
        </div>
      )}
      {saveStatus === 'error' && (
        <div className="notif-prefs-status error">
          <AlertTriangle size={16} />
          Failed to save preferences. Please try again.
        </div>
      )}

      {/* Email Notifications */}
      <div className="profile-card notif-prefs-section">
        <div className="notif-prefs-section-header">
          <div className="notif-prefs-section-title">
            <Mail size={18} />
            <div>
              <h3>Email Notifications</h3>
              <p>{emailCount} of {emailTotal} enabled</p>
            </div>
          </div>
          <div className="notif-prefs-bulk-actions">
            <button className="notif-prefs-bulk-btn" onClick={() => toggleAllForChannel('email', true)}>
              Enable All
            </button>
            <button className="notif-prefs-bulk-btn" onClick={() => toggleAllForChannel('email', false)}>
              Disable All
            </button>
          </div>
        </div>
        <div className="notif-prefs-grid">
          {Object.entries(EVENT_TYPES)
            .filter(([, meta]) => meta.channels.includes('email'))
            .map(([key, meta]) => (
              <div className="notif-prefs-toggle-row" key={`email-${key}`}>
                <div className="notif-prefs-toggle-info">
                  <span className="notif-prefs-toggle-label">{meta.label}</span>
                  <span className="notif-prefs-toggle-desc">{meta.desc}</span>
                </div>
                <button
                  className={`notif-prefs-switch ${prefs?.email?.[key] ? 'on' : 'off'}`}
                  onClick={() => togglePref('email', key)}
                  aria-label={`Toggle ${meta.label} email notifications`}
                >
                  <span className="notif-prefs-switch-track">
                    <span className="notif-prefs-switch-thumb" />
                  </span>
                </button>
              </div>
            ))}
        </div>
      </div>

      {/* Push Notifications */}
      <div className="profile-card notif-prefs-section">
        <div className="notif-prefs-section-header">
          <div className="notif-prefs-section-title">
            <Bell size={18} />
            <div>
              <h3>Push Notifications</h3>
              <p>{pushCount} of {pushTotal} enabled</p>
            </div>
          </div>
          <div className="notif-prefs-bulk-actions">
            <button className="notif-prefs-bulk-btn" onClick={() => toggleAllForChannel('push', true)}>
              Enable All
            </button>
            <button className="notif-prefs-bulk-btn" onClick={() => toggleAllForChannel('push', false)}>
              Disable All
            </button>
          </div>
        </div>
        <div className="notif-prefs-grid">
          {Object.entries(EVENT_TYPES)
            .filter(([, meta]) => meta.channels.includes('push'))
            .map(([key, meta]) => (
              <div className="notif-prefs-toggle-row" key={`push-${key}`}>
                <div className="notif-prefs-toggle-info">
                  <span className="notif-prefs-toggle-label">{meta.label}</span>
                  <span className="notif-prefs-toggle-desc">{meta.desc}</span>
                </div>
                <button
                  className={`notif-prefs-switch ${prefs?.push?.[key] ? 'on' : 'off'}`}
                  onClick={() => togglePref('push', key)}
                  aria-label={`Toggle ${meta.label} push notifications`}
                >
                  <span className="notif-prefs-switch-track">
                    <span className="notif-prefs-switch-thumb" />
                  </span>
                </button>
              </div>
            ))}
        </div>

        {/* Browser push subscription (existing component) */}
        <div className="notif-prefs-push-setup">
          <PushNotificationSettings />
        </div>
      </div>

      {/* Team Channel Integrations (Admin only) */}
      {isAdmin && (
        <div className="profile-card notif-prefs-section">
          <div className="notif-prefs-section-header">
            <div className="notif-prefs-section-title">
              <Webhook size={18} />
              <div>
                <h3>Team Channel Integrations</h3>
                <p>Send alerts to Slack, Microsoft Teams, or custom webhooks</p>
              </div>
            </div>
            <button
              className="notif-prefs-expand-btn"
              onClick={() => setWebhookExpanded(!webhookExpanded)}
            >
              {webhookExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          </div>

          {webhookExpanded && (
            <>
              {/* Info banner */}
              <div className="notif-prefs-info-banner">
                <Info size={14} />
                <span>
                  These integrations send alerts to your entire team. For Microsoft Teams, use a
                  Power Automate Workflow webhook URL. For Slack, use an Incoming Webhook URL.
                </span>
              </div>

              {/* Existing integrations */}
              {webhooks.length > 0 && (
                <div className="notif-prefs-webhooks-list">
                  {webhooks.map(wh => {
                    const config = PLATFORM_CONFIG[wh.platform] || PLATFORM_CONFIG.CUSTOM;
                    const IconComp = config.icon;
                    return (
                      <div className={`notif-prefs-webhook-card ${!wh.enabled ? 'disabled' : ''}`} key={wh.id}>
                        <div className="notif-prefs-webhook-header">
                          <div className="notif-prefs-webhook-platform" style={{ '--platform-color': config.color }}>
                            <IconComp size={16} />
                            <span>{config.label}</span>
                          </div>
                          <div className="notif-prefs-webhook-actions">
                            <button
                              className="notif-prefs-icon-btn"
                              onClick={() => handleToggleWebhook(wh.id, !wh.enabled)}
                              title={wh.enabled ? 'Disable' : 'Enable'}
                            >
                              {wh.enabled ? <ToggleRight size={20} className="text-success" /> : <ToggleLeft size={20} />}
                            </button>
                            <button
                              className="notif-prefs-icon-btn"
                              onClick={() => handleTestWebhook(wh.id)}
                              disabled={testingId === wh.id}
                              title="Send test notification"
                            >
                              {testingId === wh.id ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
                            </button>
                            <button
                              className="notif-prefs-icon-btn danger"
                              onClick={() => handleDeleteWebhook(wh.id)}
                              title="Remove integration"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                        <div className="notif-prefs-webhook-name">{wh.name}</div>
                        <div className="notif-prefs-webhook-url">
                          {wh.webhookUrl.substring(0, 60)}...
                        </div>
                        <div className="notif-prefs-webhook-events">
                          {(wh.eventTypes || []).map(et => (
                            <span className="notif-prefs-event-tag" key={et}>
                              {EVENT_TYPES[et]?.label || et}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add integration button / form */}
              {!showWebhookForm ? (
                <button
                  className="btn btn-secondary notif-prefs-add-btn"
                  onClick={() => setShowWebhookForm(true)}
                >
                  <Plus size={16} />
                  Add Integration
                </button>
              ) : (
                <WebhookForm
                  onSave={async (data) => {
                    try {
                      const res = await fetchWithTenant('/api/notification-prefs/webhooks', {
                        method: 'POST',
                        body: JSON.stringify(data),
                      });
                      const created = await res.json();
                      setWebhooks(prev => [...prev, created]);
                      setShowWebhookForm(false);
                    } catch (err) {
                      alert('Failed to create integration: ' + err.message);
                    }
                  }}
                  onCancel={() => setShowWebhookForm(false)}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================
// Webhook Integration Form (inline)
// ============================================================
const WebhookForm = ({ onSave, onCancel }) => {
  const [platform, setPlatform] = useState('TEAMS');
  const [name, setName] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [eventTypes, setEventTypes] = useState(['deviceOffline', 'alertCritical', 'systemEvents']);
  const [saving, setSaving] = useState(false);

  const toggleEvent = (key) => {
    setEventTypes(prev =>
      prev.includes(key) ? prev.filter(e => e !== key) : [...prev, key]
    );
  };

  const handleSubmit = async () => {
    if (!name.trim() || !webhookUrl.trim()) {
      alert('Name and webhook URL are required');
      return;
    }
    setSaving(true);
    await onSave({ platform, name: name.trim(), webhookUrl: webhookUrl.trim(), eventTypes });
    setSaving(false);
  };

  return (
    <div className="notif-prefs-webhook-form">
      <h4>Add Team Channel Integration</h4>

      <div className="notif-prefs-form-group">
        <label>Platform</label>
        <div className="notif-prefs-platform-select">
          {Object.entries(PLATFORM_CONFIG).map(([key, cfg]) => {
            const IconComp = cfg.icon;
            return (
              <button
                key={key}
                className={`notif-prefs-platform-option ${platform === key ? 'active' : ''}`}
                onClick={() => setPlatform(key)}
                style={{ '--platform-color': cfg.color }}
              >
                <IconComp size={16} />
                {cfg.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="notif-prefs-form-group">
        <label>Channel Name</label>
        <input
          type="text"
          className="profile-form-input"
          placeholder={platform === 'SLACK' ? '#security-alerts' : 'Ops Alerts Channel'}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="notif-prefs-form-group">
        <label>Webhook URL</label>
        <input
          type="url"
          className="profile-form-input"
          placeholder={
            platform === 'TEAMS'
              ? 'https://prod-XX.logic.azure.com:443/workflows/...'
              : platform === 'SLACK'
              ? 'https://hooks.slack.com/services/...'
              : 'https://your-endpoint.com/webhook'
          }
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
        />
        {platform === 'TEAMS' && (
          <p className="notif-prefs-form-hint">
            In Teams, go to your channel &gt; Workflows &gt; "Post to a channel when a webhook request is received"
            to get this URL.
          </p>
        )}
        {platform === 'SLACK' && (
          <p className="notif-prefs-form-hint">
            In Slack, go to your workspace settings &gt; Manage Apps &gt; Incoming Webhooks to get this URL.
          </p>
        )}
      </div>

      <div className="notif-prefs-form-group">
        <label>Event Types</label>
        <div className="notif-prefs-event-checkboxes">
          {Object.entries(EVENT_TYPES)
            .filter(([key]) => !['dailyDigest', 'weeklyReport', 'intercomCalls'].includes(key))
            .map(([key, meta]) => (
              <label className="notif-prefs-checkbox" key={key}>
                <input
                  type="checkbox"
                  checked={eventTypes.includes(key)}
                  onChange={() => toggleEvent(key)}
                />
                <span>{meta.label}</span>
              </label>
            ))}
        </div>
      </div>

      <div className="notif-prefs-form-actions">
        <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
          {saving ? <Loader2 size={14} className="spin" /> : <Plus size={14} />}
          {saving ? 'Adding...' : 'Add Integration'}
        </button>
      </div>
    </div>
  );
};

export default NotificationsTab;
